import type { Mailbox, MailboxOnConfirm, MailboxProfile, MailboxUpdate } from './types';

/** What «Editar» shows for a registered mailbox: text fields as text, the password empty. */
export interface MailboxEditForm {
  /** Only the name (contract v10.23): not the login, not where redirects leave from. */
  name: string;
  profile: MailboxProfile;
  username: string;
  password: string;
  host: string;
  port: number;
  smtp_host: string;
  smtp_port: number;
  spam_folder: string;
  authserv_id: string;
  /** What «Correcto» does with the original (contract v10.25). */
  on_confirm: MailboxOnConfirm;
  /** A payroll mailbox (contract v10.53): the only sender it reads, an address or `@dominio`. */
  payroll_sender: string;
  /** Forget the zip password remembered for next month. */
  forget_payroll_zip_password: boolean;
}

export function mailboxEditForm(mailbox: Mailbox): MailboxEditForm {
  return {
    name: mailbox.name,
    profile: mailbox.profile,
    username: mailbox.username,
    password: '',
    host: mailbox.host,
    port: mailbox.port,
    smtp_host: mailbox.smtp_host ?? '',
    smtp_port: mailbox.smtp_port,
    spam_folder: mailbox.spam_folder ?? '',
    authserv_id: mailbox.authserv_id ?? '',
    on_confirm: mailbox.on_confirm ?? 'move',
    payroll_sender: mailbox.payroll_sender ?? '',
    forget_payroll_zip_password: false,
  };
}

/**
 * Only what changed goes to the API (contract v10.17): the PATCH keeps what does not
 * come and tests the login first only when the server, the user or a password is in it,
 * and `POST /{id}/test` tests exactly this over what is stored. An empty password means
 * «keep the one there is», never «no password»; an emptied user or server is not a
 * change either (both are required). The optional text fields go back to `null`: an
 * empty spam folder is «find it on the server».
 *
 * The name (contract v10.23) is compared as the back stores it, trimmed and lowercased,
 * so retyping `Compras@…` over `compras@…` is not a change; an emptied name is not one
 * either.
 */
export function mailboxChanges(mailbox: Mailbox, form: MailboxEditForm): MailboxUpdate {
  const changes: MailboxUpdate = {};
  const orNull = (value: string) => (value.trim() ? value.trim() : null);
  const name = form.name.trim().toLowerCase();
  if (name && name !== mailbox.name) changes.name = name;
  if (form.profile !== mailbox.profile) changes.profile = form.profile;
  const username = form.username.trim();
  if (username && username !== mailbox.username) changes.username = username;
  if (form.password) changes.password = form.password;
  const host = form.host.trim();
  if (host && host !== mailbox.host) changes.host = host;
  if (form.port !== mailbox.port) changes.port = form.port;
  const smtpHost = orNull(form.smtp_host);
  if (smtpHost !== (mailbox.smtp_host ?? null)) changes.smtp_host = smtpHost;
  if (form.smtp_port !== mailbox.smtp_port) changes.smtp_port = form.smtp_port;
  const spamFolder = orNull(form.spam_folder);
  if (spamFolder !== (mailbox.spam_folder ?? null)) changes.spam_folder = spamFolder;
  const authservId = orNull(form.authserv_id);
  if (authservId !== (mailbox.authserv_id ?? null)) changes.authserv_id = authservId;
  if (form.on_confirm !== (mailbox.on_confirm ?? 'move')) changes.on_confirm = form.on_confirm;
  if (mailbox.kind === 'payroll') {
    // Emptied is a change here: `""` takes the sender off and the mailbox stops being read.
    const sender = form.payroll_sender.trim().toLowerCase();
    if (sender !== (mailbox.payroll_sender ?? '')) changes.payroll_sender = sender;
    if (form.forget_payroll_zip_password && mailbox.has_payroll_zip_password) {
      changes.forget_payroll_zip_password = true;
    }
  }
  return changes;
}

/** A name the back accepts: something before and after an `@`, and no spaces. */
export function mailboxNameIsValid(name: string): boolean {
  return /^[^\s@]+@[^\s@]+$/.test(name.trim());
}
